import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertTriangle } from "lucide-react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useState, useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useRef, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { MFAVerification } from "@/components/MFAVerification";

const emailSchema = z.string().email("Invalid email address");
const passwordSchema = z.string().min(6, "Password must be at least 6 characters");
const fullNameSchema = z.string().trim().min(1, "Full name is required").max(100, "Full name must be less than 100 characters");

const signupSchema = z.object({
  fullName: fullNameSchema,
  email: emailSchema,
  password: passwordSchema,
});

export default function Auth() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, signIn, signUp, loading, roles } = useAuth();
  const [signinEmail, setSigninEmail] = useState("");
  const [signinPassword, setSigninPassword] = useState("");
  const [signupEmail, setSignupEmail] = useState("");
  const [signupPassword, setSignupPassword] = useState("");
  const [signupFullName, setSignupFullName] = useState("");
  const [passwordStrength, setPasswordStrength] = useState<{ level: number; text: string; color: string }>({ level: 0, text: '', color: '' });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetLoading, setResetLoading] = useState(false);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaFactorId, setMfaFactorId] = useState<string | null>(null);
  const [checkingMfa, setCheckingMfa] = useState(false);
  const [emailVerified, setEmailVerified] = useState<boolean | null>(null);
  const [checkingVerification, setCheckingVerification] = useState(true);
  const [registrationsDisabled, setRegistrationsDisabled] = useState(false);
  const [checkingRegistrations, setCheckingRegistrations] = useState(true);
  const hasAttemptedRedirect = useRef(false);

  const calculatePasswordStrength = (password: string) => {
    if (password.length === 0) {
      setPasswordStrength({ level: 0, text: '', color: '' });
      return;
    }

    let strength = 0;
    
    // Length check
    if (password.length >= 8) strength++;
    if (password.length >= 12) strength++;
    
    // Character variety checks
    if (/[a-z]/.test(password)) strength++; // lowercase
    if (/[A-Z]/.test(password)) strength++; // uppercase
    if (/[0-9]/.test(password)) strength++; // numbers
    if (/[^a-zA-Z0-9]/.test(password)) strength++; // special chars

    // Determine level
    if (strength <= 2) {
      setPasswordStrength({ level: 1, text: 'Weak', color: 'bg-destructive' });
    } else if (strength <= 4) {
      setPasswordStrength({ level: 2, text: 'Medium', color: 'bg-yellow-500' });
    } else {
      setPasswordStrength({ level: 3, text: 'Strong', color: 'bg-green-500' });
    }
  };

  // Check if registrations are disabled
  useEffect(() => {
    const checkRegistrations = async () => {
      const { data } = await supabase
        .from('maintenance_settings')
        .select('registrations_disabled')
        .single();
      
      if (data) {
        setRegistrationsDisabled((data as any).registrations_disabled || false);
      }
      setCheckingRegistrations(false);
    };
    checkRegistrations();
  }, []);

  // Show success toast if redirected from email verification
  useEffect(() => {
    if (searchParams.get("verified") === "true") {
      toast({
        title: "Email Verified!",
        description: "Your email has been verified successfully. Please log in to continue.",
      });
      // Clean up the URL
      navigate("/auth", { replace: true });
    }
  }, [searchParams, navigate]);

  // Check email verification from auth.users.email_confirmed_at (single source of truth)
  useEffect(() => {
    if (!user) {
      setEmailVerified(null);
      setCheckingVerification(false);
      return;
    }
    
    // email_confirmed_at is available directly on the user object from Supabase auth
    const isVerified = !!user.email_confirmed_at;
    setEmailVerified(isVerified);
    setCheckingVerification(false);
  }, [user]);

  // Redirect handler - extracted for clarity
  const handleRedirect = useCallback(async () => {
    if (!user) return;
    
    // Navigation guard - don't redirect if already navigated away
    if (window.location.pathname !== '/auth') {
      console.log('[Auth] Already navigated away, skipping redirect');
      return;
    }
    
    console.log('[Auth] handleRedirect called', { 
      emailVerified, 
      roles, 
      userId: user.id 
    });
    
    if (emailVerified === true) {
      // Check if user is a parent first
      const { data: parentData } = await supabase
        .from('parents')
        .select('pta_id')
        .eq('user_id', user.id)
        .maybeSingle();

      console.log('[Auth] Parent check result:', parentData);

      if (parentData?.pta_id) {
        console.log('[Auth] Redirecting to fundraising portal');
        navigate(`/fundraising/${parentData.pta_id}`);
      } else if (roles.includes('site_admin')) {
        console.log('[Auth] Redirecting to site-admin');
        navigate("/site-admin");
      } else {
        console.log('[Auth] Redirecting to pta-admin');
        navigate("/pta-admin");
      }
    } else {
      // Email is NOT verified - redirect to check-email page
      console.log('[Auth] Email not verified, redirecting to check-email');
      navigate(`/check-email?email=${encodeURIComponent(user.email || '')}`);
    }
  }, [user, emailVerified, roles, navigate]);

  // Redirect based on email verification status (only if MFA is not required and not checking MFA)
  useEffect(() => {
    // Check if ALL conditions are ready
    const isReady = user && !loading && !checkingVerification && 
                    emailVerified !== null && !mfaRequired && !checkingMfa;
    
    console.log('[Auth] Redirect effect check:', {
      user: !!user,
      loading,
      checkingVerification,
      emailVerified,
      mfaRequired,
      checkingMfa,
      isReady,
      hasAttemptedRedirect: hasAttemptedRedirect.current,
      pathname: window.location.pathname
    });
    
    if (!isReady) {
      return;
    }

    // Only redirect if we're still on the auth page
    if (window.location.pathname !== '/auth') {
      console.log('[Auth] Not on /auth page, skipping redirect');
      return;
    }

    // Prevent duplicate redirect attempts
    if (hasAttemptedRedirect.current) {
      console.log('[Auth] Redirect already attempted, skipping');
      return;
    }
    hasAttemptedRedirect.current = true;

    console.log('[Auth] All conditions ready, executing redirect immediately');
    handleRedirect();
  }, [user, loading, emailVerified, checkingVerification, mfaRequired, checkingMfa, handleRedirect]);

  // Separate effect to reset redirect flag when user changes
  useEffect(() => {
    if (!user) {
      hasAttemptedRedirect.current = false;
    }
  }, [user]);

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    setCheckingMfa(true);

    try {
      emailSchema.parse(signinEmail);
      passwordSchema.parse(signinPassword);
    } catch (error: any) {
      if (error.errors) {
        const newErrors: { [key: string]: string } = {};
        error.errors.forEach((err: any) => {
          newErrors[err.path[0]] = err.message;
        });
        setErrors(newErrors);
        setCheckingMfa(false);
        return;
      }
    }

    const { error } = await signIn(signinEmail, signinPassword);
    
    if (error) {
      setCheckingMfa(false);
      return;
    }
    
    // Check if MFA is required
    const { data: factorsData } = await supabase.auth.mfa.listFactors();
    
    if (factorsData?.totp && factorsData.totp.length > 0) {
      const verifiedFactor = factorsData.totp.find(f => f.status === 'verified');
      if (verifiedFactor) {
        setMfaFactorId(verifiedFactor.id);
        setMfaRequired(true);
        // Keep checkingMfa true until MFA is complete
        return;
      }
    }
    
    // No MFA required - clear checking flag
    // The redirect useEffect will wait for loading to be false
    setCheckingMfa(false);
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    try {
      signupSchema.parse({
        fullName: signupFullName,
        email: signupEmail,
        password: signupPassword,
      });
    } catch (error: any) {
      if (error.errors) {
        const newErrors: { [key: string]: string } = {};
        error.errors.forEach((err: any) => {
          newErrors[err.path[0]] = err.message;
        });
        setErrors(newErrors);
        return;
      }
    }

    const { error, data } = await signUp(signupEmail, signupPassword, signupFullName);
    if (!error && data?.user) {
      // Send welcome email with verification link
      try {
        console.log("Sending verification email to:", signupEmail, "userId:", data.user.id);
        const response = await supabase.functions.invoke("send-welcome-email", {
          body: {
            email: signupEmail,
            fullName: signupFullName,
            userId: data.user.id,
          },
        });
        if (response.error) {
          console.error("Welcome email error:", response.error);
          toast({
            title: "Account Created",
            description: "Your account was created but we couldn't send the verification email. Please contact support.",
            variant: "destructive",
          });
        } else {
          console.log("Verification email sent successfully");
        }
      } catch (emailError) {
        console.error("Failed to send verification email:", emailError);
      }
      
      // Sign out the user - they need to verify first
      await supabase.auth.signOut();
      
      // Redirect to check email page
      navigate(`/check-email?email=${encodeURIComponent(signupEmail)}`);
    }
  };

  const handlePasswordReset = async () => {
    setResetLoading(true);
    
    try {
      emailSchema.parse(resetEmail);
    } catch (error: any) {
      toast({
        title: "Invalid Email",
        description: "Please enter a valid email address",
        variant: "destructive",
      });
      setResetLoading(false);
      return;
    }

    try {
      const response = await supabase.functions.invoke("send-password-reset", {
        body: { email: resetEmail },
      });

      if (response.error) {
        throw new Error(response.error.message);
      }

      toast({
        title: "Password Reset Email Sent",
        description: "If an account exists, you'll receive a password reset link shortly",
      });
      setResetDialogOpen(false);
      setResetEmail("");
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to send reset email",
        variant: "destructive",
      });
    }
    
    setResetLoading(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <p>Loading...</p>
      </div>
    );
  }

  // MFA verification screen
  if (mfaRequired && mfaFactorId) {
    return (
      <MFAVerification
        factorId={mfaFactorId}
        onVerified={() => {
          setMfaRequired(false);
          setMfaFactorId(null);
          setCheckingMfa(false);
          // Navigation will happen via useEffect when user state updates
        }}
        onCancel={async () => {
          await supabase.auth.signOut({ scope: 'local' });
          setMfaRequired(false);
          setMfaFactorId(null);
          setCheckingMfa(false);
        }}
      />
    );
  }

  return (
    <div className="container py-8">
      <Card className="w-full max-w-md mx-auto shadow-elevated">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl font-bold text-center">Welcome to PTA Hub</CardTitle>
          <CardDescription className="text-center">
            Sign in to your account or create a new one
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="signin" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="signin">Sign In</TabsTrigger>
              <TabsTrigger value="signup">Sign Up</TabsTrigger>
            </TabsList>
            
            <TabsContent value="signin">
              <form onSubmit={handleSignIn} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email <span className="text-destructive">*</span></Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="name@example.com"
                    value={signinEmail}
                    onChange={(e) => setSigninEmail(e.target.value)}
                    required
                  />
                  {errors.email && <p className="text-sm text-destructive">{errors.email}</p>}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Password <span className="text-destructive">*</span></Label>
                  <Input
                    id="password"
                    type="password"
                    value={signinPassword}
                    onChange={(e) => setSigninPassword(e.target.value)}
                    required
                  />
                  {errors.password && <p className="text-sm text-destructive">{errors.password}</p>}
                </div>
                <Button 
                  type="button" 
                  variant="link" 
                  className="text-sm px-0 mb-2"
                  onClick={() => setResetDialogOpen(true)}
                >
                  Forgot password?
                </Button>
                <Button type="submit" className="w-full">Sign In</Button>
              </form>
            </TabsContent>
            
            <TabsContent value="signup">
              {registrationsDisabled ? (
                <Alert variant="destructive" className="mb-4">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    New registrations are currently closed. Please check back later or contact us for more information.
                  </AlertDescription>
                </Alert>
              ) : (
                <form onSubmit={handleSignUp} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="signup-name">Full Name <span className="text-destructive">*</span></Label>
                    <Input
                      id="signup-name"
                      type="text"
                      placeholder="John Doe"
                      value={signupFullName}
                      onChange={(e) => setSignupFullName(e.target.value)}
                      required
                    />
                    {errors.fullName && <p className="text-sm text-destructive">{errors.fullName}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signup-email">Email <span className="text-destructive">*</span></Label>
                    <Input
                      id="signup-email"
                      type="email"
                      placeholder="name@example.com"
                      value={signupEmail}
                      onChange={(e) => setSignupEmail(e.target.value)}
                      required
                    />
                    {errors.email && <p className="text-sm text-destructive">{errors.email}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signup-password">Password <span className="text-destructive">*</span></Label>
                    <Input
                      id="signup-password"
                      type="password"
                      value={signupPassword}
                      onChange={(e) => {
                        setSignupPassword(e.target.value);
                        calculatePasswordStrength(e.target.value);
                      }}
                      required
                    />
                    {signupPassword.length > 0 && (
                      <div className="space-y-1">
                        <div className="flex gap-1 h-1">
                          <div className={`flex-1 rounded-full transition-colors ${passwordStrength.level >= 1 ? passwordStrength.color : 'bg-muted'}`} />
                          <div className={`flex-1 rounded-full transition-colors ${passwordStrength.level >= 2 ? passwordStrength.color : 'bg-muted'}`} />
                          <div className={`flex-1 rounded-full transition-colors ${passwordStrength.level >= 3 ? passwordStrength.color : 'bg-muted'}`} />
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Password strength: <span className={passwordStrength.level === 1 ? 'text-destructive' : passwordStrength.level === 2 ? 'text-yellow-600' : 'text-green-600'}>{passwordStrength.text}</span>
                        </p>
                      </div>
                    )}
                    {errors.password && <p className="text-sm text-destructive">{errors.password}</p>}
                  </div>
                  <Button type="submit" className="w-full">Create Account</Button>
                  <p className="text-xs text-muted-foreground text-center">
                    By signing up, you agree to our{" "}
                    <Link to="/terms" className="text-primary hover:underline">
                      Terms of Service
                    </Link>{" "}
                    and{" "}
                    <Link to="/privacy" className="text-primary hover:underline">
                      Privacy Policy
                    </Link>
                  </p>
                </form>
              )}
            </TabsContent>
          </Tabs>
          
          <div className="mt-4 text-center">
            <Link to="/" className="text-sm text-muted-foreground hover:text-primary">
              ← Back to Home
            </Link>
          </div>
        </CardContent>
      </Card>

      {/* Password Reset Dialog */}
      <Dialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
            <DialogDescription>
              Enter your email address and we'll send you a link to reset your password.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="reset-email">Email</Label>
              <Input
                id="reset-email"
                type="email"
                placeholder="name@example.com"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
              />
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setResetDialogOpen(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handlePasswordReset}
                disabled={resetLoading}
                className="flex-1"
              >
                {resetLoading ? "Sending..." : "Send Reset Link"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
